<template>
  <div class="rm">
    <header class="rm-hero">
      <div class="rm-hero-sign">
        <span class="rm-hero-shield" aria-hidden="true"><span>01</span></span>
        <div>
          <p class="rm-kicker">Brokurz Road</p>
          <h2 class="rm-title">Where completion lives</h2>
          <p class="rm-lead">
            Scroll the highway. Every exit needs checks.
            <strong>Shipped</strong> only when they’re proven in the wild.
          </p>
        </div>
      </div>
      <div class="rm-hero-actions">
        <div class="rm-top-tabs" role="tablist">
          <button
            type="button"
            class="rm-top-tab"
            :class="{ 'is-on': pageTab === 'road' }"
            role="tab"
            @click="pageTab = 'road'"
          >Road</button>
          <button
            type="button"
            class="rm-top-tab"
            :class="{ 'is-on': pageTab === 'notes' }"
            role="tab"
            @click="pageTab = 'notes'"
          >Notes</button>
        </div>
        <button v-if="pageTab === 'road'" type="button" class="rm-add" @click="openCreate('now')">
          <i class="pi pi-plus" aria-hidden="true" />
          Add stop
        </button>
      </div>
    </header>

    <Message v-if="error" severity="error" :closable="false" class="tw-mb-3">{{ error }}</Message>

    <RoadmapNotes v-if="pageTab === 'notes'" />

    <div v-else-if="loading && !items.length" class="rm-loading">
      <ProgressSpinner style="width: 2rem; height: 2rem" />
    </div>

    <section v-else class="rm-drive" aria-label="Brokurz Road">
      <div class="rm-sky" aria-hidden="true">
        <span class="rm-sun" />
        <span class="rm-cloud rm-cloud--a" />
        <span class="rm-cloud rm-cloud--b" />
      </div>

      <div class="rm-tabs" role="tablist">
        <button
          v-for="lane in lanes"
          :key="lane.id"
          type="button"
          class="rm-tab"
          :class="{ 'is-active': highlightLane === lane.id }"
          role="tab"
          @click="scrollToLane(lane.id)"
        >
          <span>{{ lane.label }}</span>
          <span class="rm-tab-count">{{ itemsInLane(lane.id).length }}</span>
        </button>
        <span class="rm-tabs-meta">{{ shippedCount }}/{{ items.length }} shipped</span>
      </div>

      <div ref="scroller" class="rm-scroller" @scroll="onScroll">
        <div class="rm-track" :style="{ width: trackWidthPx + 'px' }">
          <div class="rm-horizon" aria-hidden="true" />
          <div class="rm-shoulder rm-shoulder--top" aria-hidden="true" />
          <div class="rm-shoulder rm-shoulder--bot" aria-hidden="true" />

          <div class="rm-road" aria-hidden="true">
            <div class="rm-road-edge rm-road-edge--l" />
            <div class="rm-road-edge rm-road-edge--r" />
            <div class="rm-road-dash" />
          </div>

          <div class="rm-car-wrap" :style="{ left: carLeftPx + 'px' }" aria-hidden="true">
            <svg class="rm-car" viewBox="0 0 160 64" xmlns="http://www.w3.org/2000/svg">
              <ellipse cx="80" cy="58" rx="50" ry="3.5" fill="rgba(24,24,27,0.12)" />
              <path d="M28 42c4-14 18-24 36-26h28c14 1 24 6 30 16l10 10H22l6-0z" fill="#f4f4f5" />
              <path d="M28 42c4-14 18-24 36-26h28c14 1 24 6 30 16l10 10H22l6-0z" fill="none" stroke="#d4d4d8" stroke-width="1" />
              <path d="M42 28h48c8 0 14 3 18 8H38c2-4 4-8 4-8z" fill="#e4e4e7" />
              <path d="M48 30h18v10H46c1-4 2-8 2-10zm28 0h22c2 2 4 5 5 10H76V30z" fill="#a1a1aa" opacity="0.35" />
              <rect x="18" y="40" width="124" height="8" rx="2" fill="#27272a" />
              <circle cx="48" cy="50" r="9" fill="#18181b" />
              <circle cx="48" cy="50" r="4" fill="#a1a1aa" />
              <circle cx="118" cy="50" r="9" fill="#18181b" />
              <circle cx="118" cy="50" r="4" fill="#a1a1aa" />
              <rect x="132" y="42" width="8" height="4" rx="1" fill="#fafafa" opacity="0.9" />
              <rect x="20" y="42" width="6" height="3" rx="1" fill="#ef4444" />
              <text x="72" y="46" text-anchor="middle" fill="#fafafa" font-size="7" font-weight="800" font-family="Public Sans, system-ui, sans-serif">BK</text>
            </svg>
          </div>

          <div
            v-for="(seg, sIdx) in segments"
            :key="seg.id"
            :ref="'lane-' + seg.id"
            class="rm-segment"
            :style="{ left: seg.leftPx + 'px', width: seg.widthPx + 'px' }"
          >
            <div class="rm-milepost">
              <span class="rm-milepost-route">{{ seg.route }}</span>
              <span class="rm-milepost-name">{{ seg.label }}</span>
              <span class="rm-milepost-hint">{{ seg.hint }}</span>
            </div>

            <div v-if="!seg.items.length" class="rm-empty">
              <button type="button" class="rm-empty-btn" @click="openCreate(seg.id)">
                <i class="pi pi-plus" aria-hidden="true" />
                Drop a stop
              </button>
            </div>

            <article
              v-for="(item, idx) in seg.items"
              :key="item.id"
              class="rm-stop"
              :class="[idx % 2 === 0 ? 'is-above' : 'is-below', 'is-' + item.status]"
              :style="{ left: stopLeft(idx, seg.items.length) }"
            >
              <div class="rm-post" aria-hidden="true" />
              <button type="button" class="rm-exit" @click="openEdit(item)">
                <div class="rm-exit-top">
                  <span class="rm-exit-code">EXIT {{ mileFor(sIdx, idx) }}</span>
                  <span class="rm-exit-checks" :class="{ 'is-done': dod(item).complete }">
                    {{ dod(item).done }}/{{ dod(item).total || '—' }}
                  </span>
                </div>
                <h3 class="rm-exit-title">{{ item.title }}</h3>
                <p v-if="item.doneMeans" class="rm-exit-means">{{ item.doneMeans }}</p>
                <div class="rm-exit-bar" aria-hidden="true">
                  <span :style="{ width: dod(item).pct + '%' }" />
                </div>
                <div class="rm-exit-meta">
                  <span class="rm-exit-status">{{ statusLabel(item.status) }}</span>
                  <span v-if="item.owner" class="rm-exit-owner">{{ item.owner }}</span>
                </div>
              </button>
              <button type="button" class="rm-node" @click="openEdit(item)" :aria-label="item.title">
                <span class="rm-node-ring" :class="'is-' + item.status" />
              </button>
            </article>
          </div>

          <div class="rm-end">
            <div class="rm-end-sign">HORIZON</div>
            <p>IDX · search · multi-MLS</p>
          </div>
        </div>
      </div>

      <footer class="rm-drive-foot">
        <span>Drag / scroll to drive →</span>
        <span>Exit checks = the ship contract</span>
      </footer>
    </section>

    <Dialog
      :visible.sync="dialogVisible"
      :modal="true"
      :closable="true"
      :dismissableMask="true"
      class="rm-dialog"
      :style="{ width: 'min(40rem, calc(100vw - 1.5rem))' }"
    >
      <template #header>
        <div class="rm-dlg-sign">
          <span class="rm-dlg-shield" aria-hidden="true">
            <span class="rm-dlg-route">01</span>
          </span>
          <div class="rm-dlg-sign-copy">
            <span class="rm-dlg-kicker">{{ editingId ? 'Edit mile marker' : 'New mile marker' }}</span>
            <span class="rm-dlg-title">{{ editingId ? 'Edit stop' : 'Add stop' }}</span>
          </div>
        </div>
      </template>

      <div class="rm-dlg">
        <section class="rm-dlg-section">
          <div class="rm-dlg-field">
            <label class="rm-dlg-label" for="rm-title">Title</label>
            <InputText
              id="rm-title"
              v-model="form.title"
              class="rm-dlg-input tw-w-full"
              placeholder="e.g. Render production deploy"
            />
          </div>
          <div class="rm-dlg-field">
            <label class="rm-dlg-label" for="rm-desc">What this unlocks</label>
            <Textarea
              id="rm-desc"
              v-model="form.description"
              rows="2"
              class="rm-dlg-input tw-w-full"
              placeholder="Short context for the crew"
            />
          </div>
          <div class="rm-dlg-field">
            <label class="rm-dlg-label" for="rm-means">Done means</label>
            <Textarea
              id="rm-means"
              v-model="form.doneMeans"
              rows="2"
              class="rm-dlg-input tw-w-full"
              placeholder="Done means: ___ in the wild — demoable, ops-owned"
            />
            <p class="rm-dlg-hint">Code merged ≠ done. This sentence is the bar.</p>
          </div>
          <div class="rm-dlg-field">
            <label class="rm-dlg-label" for="rm-owner">Owner</label>
            <InputText
              id="rm-owner"
              v-model="form.owner"
              class="rm-dlg-input tw-w-full"
              placeholder="Who owns proving this"
            />
          </div>
        </section>

        <section class="rm-dlg-checks">
          <div class="rm-dlg-checks-head">
            <div>
              <h3 class="rm-dlg-checks-title">Exit checks</h3>
              <p class="rm-dlg-hint">Binary proof. Shipped unlocks only when all are checked.</p>
            </div>
            <span class="rm-dlg-pill" :class="{ 'is-ready': canShip }">
              {{ formDoneCount }}/{{ form.checklist.filter(c => c.text.trim()).length || form.checklist.length }}
            </span>
          </div>
          <ul class="rm-dlg-check-list">
            <li v-for="(row, idx) in form.checklist" :key="row.id || idx" class="rm-dlg-check-row">
              <Checkbox v-model="row.done" :binary="true" class="rm-dlg-check" />
              <InputText v-model="row.text" class="rm-dlg-input tw-w-full" placeholder="Prove it in the wild…" />
              <button type="button" class="rm-dlg-x" aria-label="Remove check" @click="removeCheck(idx)">
                <i class="pi pi-times" aria-hidden="true" />
              </button>
            </li>
          </ul>
          <button
            v-if="form.checklist.length < 7"
            type="button"
            class="rm-dlg-add-check"
            @click="addCheck"
          >
            <i class="pi pi-plus" aria-hidden="true" />
            Add check
          </button>
        </section>

        <section class="rm-dlg-meta">
          <div class="rm-dlg-field">
            <span class="rm-dlg-label">Lane</span>
            <div class="rm-seg" role="group" aria-label="Lane">
              <button
                v-for="opt in laneOptions"
                :key="opt.value"
                type="button"
                class="rm-seg-btn"
                :class="{ 'is-on': form.lane === opt.value }"
                @click="form.lane = opt.value"
              >{{ opt.label }}</button>
            </div>
          </div>
          <div class="rm-dlg-field">
            <span class="rm-dlg-label">Status</span>
            <div class="rm-seg" role="group" aria-label="Status">
              <button
                v-for="opt in statusOptionsForForm"
                :key="opt.value"
                type="button"
                class="rm-seg-btn"
                :class="{
                  'is-on': form.status === opt.value,
                  'is-ship': opt.value === 'shipped',
                  'is-disabled': opt.disabled,
                }"
                :disabled="opt.disabled"
                @click="!opt.disabled && (form.status = opt.value)"
              >{{ opt.label }}</button>
            </div>
            <p v-if="!canShip" class="rm-dlg-hint">
              Shipped opens when every exit check is complete.
            </p>
          </div>
        </section>
      </div>

      <template #footer>
        <div class="rm-dlg-footer">
          <button
            v-if="editingId"
            type="button"
            class="rm-dlg-danger"
            :disabled="deleting"
            @click="remove"
          >
            <i class="pi pi-trash" aria-hidden="true" />
            Delete
          </button>
          <span class="rm-dlg-footer-spacer" />
          <button type="button" class="rm-dlg-ghost" @click="dialogVisible = false">Cancel</button>
          <button type="button" class="rm-dlg-primary" :disabled="saving" @click="save">
            <i class="pi pi-check" aria-hidden="true" />
            {{ saving ? 'Saving…' : 'Save stop' }}
          </button>
        </div>
      </template>
    </Dialog>
  </div>
</template>

<script>
import gql from 'graphql-tag'
import RoadmapNotes from './RoadmapNotes'

const LIST_QUERY = gql`
  query RoadmapItems {
    roadmapItems {
      id
      title
      description
      doneMeans
      owner
      checklist { id text done }
      status
      lane
      sortOrder
      updatedAt
      updatedBy
    }
  }
`

const SAVE_MUTATION = gql`
  mutation SaveRoadmapItem($input: RoadmapItemInput!) {
    saveRoadmapItem(input: $input) {
      id
      title
      description
      doneMeans
      owner
      checklist { id text done }
      status
      lane
      sortOrder
      updatedAt
      updatedBy
    }
  }
`

const DELETE_MUTATION = gql`
  mutation DeleteRoadmapItem($id: ID!) {
    deleteRoadmapItem(id: $id)
  }
`

const LANE_META = [
  { id: 'now', label: 'Now', route: '01', hint: 'In motion', minWidth: 480 },
  { id: 'next', label: 'Next', route: '02', hint: 'Queued up', minWidth: 480 },
  { id: 'later', label: 'Later', route: '03', hint: 'Horizon', minWidth: 480 },
  { id: 'done', label: 'Done', route: '04', hint: 'Shipped', minWidth: 400 },
]

let checkSeq = 0
function newCheck(text = '', done = false) {
  checkSeq += 1
  return { id: `local-${Date.now()}-${checkSeq}`, text, done }
}

function dodStats(item) {
  const list = (item && item.checklist) || []
  const total = list.length
  const done = list.filter(c => c.done).length
  return {
    total,
    done,
    pct: total ? Math.round((done / total) * 100) : 0,
    complete: total > 0 && done === total,
  }
}

export default {
  components: { RoadmapNotes },
  data() {
    return {
      pageTab: 'road',
      items: [],
      loading: false,
      saving: false,
      deleting: false,
      error: null,
      dialogVisible: false,
      editingId: null,
      highlightLane: 'now',
      scrollX: 0,
      form: {
        title: '',
        description: '',
        doneMeans: '',
        owner: '',
        status: 'planned',
        lane: 'now',
        checklist: [newCheck(), newCheck(), newCheck()],
      },
      lanes: LANE_META,
      laneOptions: [
        { label: 'Now', value: 'now' },
        { label: 'Next', value: 'next' },
        { label: 'Later', value: 'later' },
        { label: 'Done', value: 'done' },
      ],
    }
  },
  computed: {
    segments() {
      let cursor = 48
      return this.lanes.map((lane) => {
        const items = this.itemsInLane(lane.id)
        const widthPx = Math.max(lane.minWidth, 200 + items.length * 200)
        const seg = { ...lane, items, leftPx: cursor, widthPx }
        cursor += widthPx
        return seg
      })
    },
    trackWidthPx() {
      const last = this.segments[this.segments.length - 1]
      return (last ? last.leftPx + last.widthPx : 720) + 220
    },
    progressPct() {
      if (!this.items.length) return 8
      const shipped = this.items.filter(i => i.status === 'shipped').length
      return Math.max(8, Math.round((shipped / this.items.length) * 100))
    },
    shippedCount() {
      return this.items.filter(i => i.status === 'shipped').length
    },
    carLeftPx() {
      // Keep the car in-frame while scrolling the highway
      return Math.max(72, this.scrollX + 110)
    },
    formDoneCount() {
      return this.form.checklist.filter(c => c.done && c.text.trim()).length
    },
    canShip() {
      const rows = this.form.checklist.filter(c => c.text.trim())
      return rows.length > 0 && rows.every(c => c.done)
    },
    statusOptionsForForm() {
      return [
        { label: 'Planned', value: 'planned' },
        { label: 'Building', value: 'building' },
        { label: 'Shipped', value: 'shipped', disabled: !this.canShip },
      ]
    },
  },
  watch: {
    'form.status'(val) {
      if (val === 'shipped' && !this.canShip) {
        this.form.status = 'building'
        this.$toast.add({
          severity: 'warn',
          summary: 'Not shippable yet',
          detail: 'Check every exit item first.',
          life: 2800,
        })
      }
      if (val === 'shipped') this.form.lane = 'done'
    },
    'form.lane'(val) {
      if (val === 'done' && this.form.status !== 'shipped') {
        if (this.canShip) this.form.status = 'shipped'
        else {
          this.form.lane = this.editingId ? (this.items.find(i => i.id === this.editingId) || {}).lane || 'now' : 'now'
          this.$toast.add({
            severity: 'warn',
            summary: 'Done lane = Shipped',
            detail: 'Finish exit checks before moving to Done.',
            life: 2800,
          })
        }
      }
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    dod: dodStats,
    itemsInLane(laneId) {
      return this.items.filter(i => i.lane === laneId)
    },
    stopLeft(idx, total) {
      if (total <= 1) return '48%'
      return `${20 + (idx / Math.max(total - 1, 1)) * 60}%`
    },
    mileFor(segIdx, idx) {
      return `${(segIdx + 1) * 10 + idx}`
    },
    statusLabel(s) {
      return ({ planned: 'Planned', building: 'Building', shipped: 'Shipped' })[s] || s
    },
    addCheck() {
      if (this.form.checklist.length >= 7) return
      this.form.checklist.push(newCheck())
    },
    removeCheck(idx) {
      this.form.checklist.splice(idx, 1)
    },
    scrollToLane(id) {
      this.highlightLane = id
      const refs = this.$refs[`lane-${id}`]
      const el = Array.isArray(refs) ? refs[0] : refs
      const scroller = this.$refs.scroller
      if (el && scroller) {
        scroller.scrollTo({ left: Math.max(0, el.offsetLeft - 40), behavior: 'smooth' })
      }
    },
    onScroll() {
      const scroller = this.$refs.scroller
      if (!scroller) return
      this.scrollX = scroller.scrollLeft
      const x = scroller.scrollLeft + scroller.clientWidth * 0.33
      let current = this.segments[0] && this.segments[0].id
      this.segments.forEach((seg) => {
        if (x >= seg.leftPx) current = seg.id
      })
      this.highlightLane = current
    },
    openCreate(lane) {
      this.editingId = null
      this.form = {
        title: '',
        description: '',
        doneMeans: '',
        owner: '',
        status: 'planned',
        lane: typeof lane === 'string' ? lane : 'now',
        checklist: [newCheck(), newCheck(), newCheck()],
      }
      this.dialogVisible = true
    },
    openEdit(item) {
      this.editingId = item.id
      const list = (item.checklist || []).map(c => ({
        id: c.id,
        text: c.text,
        done: !!c.done,
      }))
      this.form = {
        title: item.title,
        description: item.description || '',
        doneMeans: item.doneMeans || '',
        owner: item.owner || '',
        status: item.status,
        lane: item.lane,
        checklist: list.length ? list : [newCheck(), newCheck(), newCheck()],
      }
      this.dialogVisible = true
    },
    async load() {
      this.loading = true
      this.error = null
      try {
        const { data } = await this.$apollo.query({
          query: LIST_QUERY,
          fetchPolicy: 'network-only',
        })
        this.items = data.roadmapItems || []
        this.$nextTick(() => this.onScroll())
      } catch (err) {
        this.error = err.message
      } finally {
        this.loading = false
      }
    },
    async save() {
      if (!this.form.title.trim()) {
        this.$toast.add({ severity: 'warn', summary: 'Title required', life: 2500 })
        return
      }
      const checklist = this.form.checklist
        .map(c => ({ id: c.id, text: (c.text || '').trim(), done: !!c.done }))
        .filter(c => c.text)
      if (this.form.status === 'shipped' && (checklist.length === 0 || !checklist.every(c => c.done))) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Not shippable yet',
          detail: 'Finish every exit check first.',
          life: 3000,
        })
        return
      }
      this.saving = true
      try {
        await this.$apollo.mutate({
          mutation: SAVE_MUTATION,
          variables: {
            input: {
              ...(this.editingId ? { id: this.editingId } : {}),
              title: this.form.title.trim(),
              description: this.form.description,
              doneMeans: this.form.doneMeans,
              owner: this.form.owner,
              checklist,
              status: this.form.status,
              lane: this.form.lane,
            },
          },
        })
        this.dialogVisible = false
        this.$toast.add({ severity: 'success', summary: 'Road updated', life: 2000 })
        await this.load()
      } catch (err) {
        this.$toast.add({ severity: 'error', summary: 'Save failed', detail: err.message, life: 4000 })
      } finally {
        this.saving = false
      }
    },
    async remove() {
      if (!this.editingId) return
      const ok = window.confirm('Remove this stop from the road?')
      if (!ok) return
      this.deleting = true
      try {
        await this.$apollo.mutate({
          mutation: DELETE_MUTATION,
          variables: { id: this.editingId },
        })
        this.dialogVisible = false
        this.$toast.add({ severity: 'success', summary: 'Stop removed', life: 2000 })
        await this.load()
      } catch (err) {
        this.$toast.add({ severity: 'error', summary: 'Delete failed', detail: err.message, life: 4000 })
      } finally {
        this.deleting = false
      }
    },
  },
}
</script>

<style scoped>
.rm {
  position: relative;
  margin: -1.15rem -1.35rem -2rem;
  min-height: calc(100% + 3.15rem);
  padding: 1.15rem 1.35rem 1.75rem;
  color: #18181b;
  background:
    radial-gradient(ellipse 90% 55% at 50% -5%, #f8fafc 0%, #ffffff 52%, #f4f4f5 100%);
  overflow: hidden;
}

.rm-hero {
  position: relative;
  z-index: 3;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
}

.rm-hero-actions {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex-wrap: wrap;
}

.rm-top-tabs {
  display: inline-flex;
  padding: 0.2rem;
  border-radius: 999px;
  border: 1px solid #e4e4e7;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
}

.rm-top-tab {
  border: 0;
  border-radius: 999px;
  padding: 0.4rem 0.85rem;
  background: transparent;
  color: #71717a;
  font-size: 0.78rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
}

.rm-top-tab.is-on {
  background: #18181b;
  color: #fff;
}

.rm-hero-sign {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  max-width: 40rem;
}

.rm-hero-shield {
  flex-shrink: 0;
  width: 2.35rem;
  height: 2.75rem;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 0.35rem;
  background: linear-gradient(180deg, #fff 0 38%, #dc2626 38% 100%);
  clip-path: polygon(50% 0, 100% 8%, 100% 62%, 50% 100%, 0 62%, 0 8%);
  margin-top: 0.15rem;
  box-shadow: 0 4px 12px rgba(24, 24, 27, 0.08);
}

.rm-hero-shield span {
  font-size: 0.68rem;
  font-weight: 800;
  color: #18181b;
  line-height: 1;
}

.rm-kicker {
  margin: 0;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #a1a1aa;
}

.rm-title {
  margin: 0.2rem 0 0.35rem;
  font-size: 1.75rem;
  font-weight: 750;
  letter-spacing: -0.045em;
  color: #09090b;
}

.rm-lead {
  margin: 0;
  font-size: 0.92rem;
  line-height: 1.5;
  color: #71717a;
}

.rm-lead strong {
  color: #18181b;
}

.rm-add {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.55rem 0.95rem;
  border: 1px solid #18181b;
  border-radius: 999px;
  background: #18181b;
  color: #fff;
  font-size: 0.82rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.15s ease;
}

.rm-add:hover {
  background: #09090b;
  transform: translateY(-1px);
}

.rm-loading {
  display: flex;
  justify-content: center;
  padding: 4rem;
}

.rm-drive {
  position: relative;
  z-index: 2;
}

.rm-sky {
  pointer-events: none;
  position: absolute;
  inset: 0 0 auto 0;
  height: 9rem;
  z-index: 0;
  overflow: hidden;
}

.rm-sun {
  position: absolute;
  top: 1rem;
  right: 14%;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 50%;
  background: radial-gradient(circle at 40% 40%, #fef9c3, #fde047 55%, #facc15 100%);
  box-shadow: 0 0 36px rgba(250, 204, 21, 0.35);
}

.rm-cloud {
  position: absolute;
  height: 0.85rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 2px 10px rgba(24, 24, 27, 0.04);
}

.rm-cloud--a {
  top: 2.2rem;
  left: 18%;
  width: 5.5rem;
}

.rm-cloud--b {
  top: 3.4rem;
  left: 42%;
  width: 3.8rem;
  opacity: 0.8;
}

.rm-tabs {
  position: relative;
  z-index: 4;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25rem 0.1rem 0.85rem;
  overflow-x: auto;
}

.rm-tab {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.75rem;
  border: 1px solid #e4e4e7;
  border-radius: 999px;
  background: #fff;
  color: #71717a;
  font-size: 0.78rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.rm-tab:hover {
  color: #18181b;
  border-color: #d4d4d8;
}

.rm-tab.is-active {
  background: #18181b;
  border-color: #18181b;
  color: #fff;
}

.rm-tab-count {
  min-width: 1.1rem;
  padding: 0 0.28rem;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.06);
  font-size: 0.66rem;
  font-weight: 800;
  text-align: center;
}

.rm-tab.is-active .rm-tab-count {
  background: rgba(255, 255, 255, 0.18);
}

.rm-tabs-meta {
  margin-left: auto;
  font-size: 0.72rem;
  font-weight: 700;
  color: #a1a1aa;
  white-space: nowrap;
}

.rm-scroller {
  position: relative;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  cursor: grab;
  border-radius: 18px;
  border: 1px solid #e4e4e7;
  background:
    linear-gradient(180deg, #eff6ff 0%, #f8fafc 28%, #f4f4f5 100%);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
}

.rm-scroller:active {
  cursor: grabbing;
}

.rm-track {
  position: relative;
  height: 30rem;
  min-width: 100%;
}

.rm-horizon {
  position: absolute;
  left: 0;
  right: 0;
  top: 38%;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(161, 161, 170, 0.35), transparent);
  z-index: 0;
}

.rm-shoulder {
  position: absolute;
  left: 0;
  right: 0;
  height: 1.15rem;
  z-index: 1;
  background: repeating-linear-gradient(
    90deg,
    #e7e5e4 0 8px,
    #d6d3d1 8px 16px
  );
  opacity: 0.7;
}

.rm-shoulder--top {
  top: calc(50% - 3.15rem);
}

.rm-shoulder--bot {
  top: calc(50% + 2rem);
}

.rm-road {
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 4rem;
  transform: translateY(-50%);
  z-index: 2;
  background:
    linear-gradient(180deg, #a1a1aa 0%, #71717a 14%, #52525b 50%, #71717a 86%, #a1a1aa 100%);
  box-shadow:
    0 10px 30px rgba(24, 24, 27, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
}

.rm-road-edge {
  position: absolute;
  left: 0;
  right: 0;
  height: 3px;
  background: #fafafa;
  opacity: 0.95;
}

.rm-road-edge--l { top: 6px; }
.rm-road-edge--r { bottom: 6px; }

.rm-road-dash {
  position: absolute;
  left: 1rem;
  right: 1rem;
  top: 50%;
  height: 4px;
  transform: translateY(-50%);
  background: repeating-linear-gradient(
    90deg,
    #fafafa 0 28px,
    transparent 28px 52px
  );
  opacity: 0.95;
  animation: dash-move 0.75s linear infinite;
}

@keyframes dash-move {
  from { background-position: 0 0; }
  to { background-position: 52px 0; }
}

.rm-car-wrap {
  position: absolute;
  top: 50%;
  z-index: 6;
  width: 9.5rem;
  transform: translate(-20%, -62%);
  pointer-events: none;
  transition: left 0.08s linear;
}

.rm-car {
  position: relative;
  width: 100%;
  height: auto;
  filter: drop-shadow(0 8px 12px rgba(24, 24, 27, 0.18));
}

.rm-segment {
  position: absolute;
  top: 0;
  bottom: 0;
  z-index: 4;
}

.rm-milepost {
  position: absolute;
  top: 1rem;
  left: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  padding: 0.5rem 0.7rem;
  border-radius: 10px;
  background: #fff;
  border: 1px solid #e4e4e7;
  box-shadow: 0 8px 24px rgba(24, 24, 27, 0.06);
  min-width: 5.5rem;
}

.rm-milepost-route {
  font-size: 0.58rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  color: #a1a1aa;
}

.rm-milepost-name {
  font-size: 1rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  color: #18181b;
}

.rm-milepost-hint {
  font-size: 0.68rem;
  color: #71717a;
}

.rm-empty {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, 2.8rem);
}

.rm-empty-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.4rem 0.75rem;
  border-radius: 999px;
  border: 1px dashed #d4d4d8;
  background: #fff;
  color: #52525b;
  font-size: 0.75rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
}

.rm-empty-btn:hover {
  border-color: #18181b;
  color: #18181b;
}

.rm-stop {
  position: absolute;
  width: 14rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  transform: translateX(-50%);
}

.rm-stop.is-above {
  bottom: calc(50% + 2.15rem);
}

.rm-stop.is-below {
  top: calc(50% + 2.15rem);
  flex-direction: column-reverse;
}

.rm-post {
  width: 3px;
  height: 1.05rem;
  background: linear-gradient(#d4d4d8, #a1a1aa);
}

.rm-node {
  position: relative;
  z-index: 5;
  width: 1.15rem;
  height: 1.15rem;
  border: 0;
  padding: 0;
  background: transparent;
  cursor: pointer;
  margin: 0.1rem 0;
}

.rm-node-ring {
  display: block;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 2px solid #18181b;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(24, 24, 27, 0.08);
}

.rm-node-ring.is-building {
  border-color: #ca8a04;
  background: #fef9c3;
}

.rm-node-ring.is-shipped {
  border-color: #15803d;
  background: #dcfce7;
}

.rm-exit {
  width: 100%;
  text-align: left;
  border: 1px solid #e4e4e7;
  border-radius: 12px;
  background: #fff;
  padding: 0.65rem 0.75rem 0.7rem;
  box-shadow: 0 10px 28px rgba(24, 24, 27, 0.06);
  cursor: pointer;
  font-family: inherit;
  color: #18181b;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}

.rm-exit:hover {
  transform: translateY(-2px);
  border-color: #d4d4d8;
  box-shadow: 0 16px 36px rgba(24, 24, 27, 0.1);
}

.rm-stop.is-shipped .rm-exit {
  border-color: #bbf7d0;
  background: linear-gradient(180deg, #fff 0%, #f0fdf4 100%);
}

.rm-stop.is-building .rm-exit {
  border-color: #fde68a;
  background: linear-gradient(180deg, #fff 0%, #fffbeb 100%);
}

.rm-exit-top {
  display: flex;
  justify-content: space-between;
  gap: 0.35rem;
  margin-bottom: 0.25rem;
}

.rm-exit-code {
  font-size: 0.58rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  color: #a1a1aa;
}

.rm-exit-checks {
  font-size: 0.68rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: #71717a;
}

.rm-exit-checks.is-done {
  color: #15803d;
}

.rm-exit-title {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 800;
  letter-spacing: -0.025em;
  line-height: 1.25;
}

.rm-exit-means {
  margin: 0.3rem 0 0;
  font-size: 0.72rem;
  line-height: 1.4;
  color: #71717a;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.rm-exit-bar {
  margin-top: 0.5rem;
  height: 3px;
  border-radius: 2px;
  background: #f4f4f5;
  overflow: hidden;
}

.rm-exit-bar span {
  display: block;
  height: 100%;
  background: #18181b;
  border-radius: 2px;
}

.rm-exit-meta {
  margin-top: 0.4rem;
  display: flex;
  justify-content: space-between;
  gap: 0.35rem;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #a1a1aa;
}

.rm-end {
  position: absolute;
  top: 50%;
  right: 1.5rem;
  transform: translateY(-50%);
  text-align: right;
  z-index: 3;
}

.rm-end-sign {
  display: inline-block;
  margin-bottom: 0.3rem;
  padding: 0.3rem 0.55rem;
  border: 1px solid #e4e4e7;
  border-radius: 8px;
  background: #fff;
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  color: #52525b;
  box-shadow: 0 4px 14px rgba(24, 24, 27, 0.05);
}

.rm-end p {
  margin: 0;
  font-size: 0.72rem;
  color: #a1a1aa;
}

.rm-drive-foot {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  padding: 0.75rem 0.15rem 0;
  color: #a1a1aa;
  font-size: 0.72rem;
  font-weight: 600;
}

@media (max-width: 700px) {
  .rm {
    margin: -1rem -1rem -2rem;
    padding: 1rem 1rem 1.25rem;
  }

  .rm-track {
    height: 34rem;
  }

  .rm-stop {
    width: 12.25rem;
  }

  .rm-title {
    font-size: 1.4rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .rm-road-dash {
    animation: none;
  }
}
</style>

<!-- Dialog is teleported to body — keep styles unscoped -->
<style>
.rm-dialog.p-dialog {
  border: 1px solid #e4e4e7 !important;
  border-radius: 16px !important;
  box-shadow: 0 24px 64px rgba(24, 24, 27, 0.12) !important;
  overflow: hidden;
  font-family: 'Public Sans', system-ui, sans-serif;
}

.rm-dialog .p-dialog-header {
  padding: 0 !important;
  border-bottom: 0 !important;
  background: transparent !important;
}

.rm-dialog .p-dialog-header-icons {
  position: absolute;
  top: 0.65rem;
  right: 0.65rem;
  z-index: 2;
}

.rm-dialog .p-dialog-header-icon {
  width: 2rem;
  height: 2rem;
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.85) !important;
  background: rgba(0, 0, 0, 0.18) !important;
}

.rm-dialog .p-dialog-header-icon:hover {
  background: rgba(0, 0, 0, 0.28) !important;
  color: #fff !important;
}

.rm-dialog .p-dialog-content {
  padding: 1.1rem 1.15rem 0.35rem !important;
  background: #fff !important;
}

.rm-dialog .p-dialog-footer {
  padding: 0.85rem 1.15rem 1.05rem !important;
  border-top: 1px solid #f4f4f5 !important;
  background: #fff !important;
}

.rm-dlg-sign {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  width: 100%;
  margin: 0;
  padding: 1rem 3rem 1rem 1.1rem;
  background: linear-gradient(180deg, #166534 0%, #14532d 100%);
  border-bottom: 3px solid #fafafa;
  box-shadow: inset 0 0 0 2px #166534;
  color: #fff;
}

.rm-dlg-shield {
  flex-shrink: 0;
  width: 2.15rem;
  height: 2.5rem;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 0.32rem;
  background: linear-gradient(180deg, #fff 0 38%, #dc2626 38% 100%);
  clip-path: polygon(50% 0, 100% 8%, 100% 62%, 50% 100%, 0 62%, 0 8%);
}

.rm-dlg-route {
  font-size: 0.62rem;
  font-weight: 800;
  color: #18181b;
  line-height: 1;
}

.rm-dlg-sign-copy {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
}

.rm-dlg-kicker {
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.72);
}

.rm-dlg-title {
  font-size: 1.15rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.15;
}

.rm-dlg {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.rm-dlg-section,
.rm-dlg-meta {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.rm-dlg-field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.rm-dlg-label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #71717a;
}

.rm-dlg-hint {
  margin: 0;
  font-size: 0.72rem;
  line-height: 1.4;
  color: #a1a1aa;
}

.rm-dlg .rm-dlg-input.p-inputtext,
.rm-dlg .rm-dlg-input.p-inputtextarea,
.rm-dialog .rm-dlg-input.p-inputtext,
.rm-dialog .rm-dlg-input.p-inputtextarea {
  border: 1px solid #e4e4e7 !important;
  border-radius: 8px !important;
  background: #fff !important;
  color: #18181b !important;
  font-family: inherit !important;
  font-size: 0.9rem !important;
  padding: 0.55rem 0.7rem !important;
  box-shadow: none !important;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.rm-dialog .rm-dlg-input.p-inputtext:focus,
.rm-dialog .rm-dlg-input.p-inputtextarea:focus {
  border-color: #18181b !important;
  box-shadow: 0 0 0 3px rgba(24, 24, 27, 0.08) !important;
}

.rm-dlg-checks {
  padding: 0.85rem;
  border-radius: 12px;
  border: 1px solid #e4e4e7;
  background:
    linear-gradient(180deg, #fafafa 0%, #fff 40%),
    repeating-linear-gradient(
      90deg,
      transparent 0 10px,
      rgba(24, 24, 27, 0.03) 10px 11px
    );
}

.rm-dlg-checks-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.65rem;
}

.rm-dlg-checks-title {
  margin: 0 0 0.15rem;
  font-size: 0.82rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #18181b;
}

.rm-dlg-pill {
  flex-shrink: 0;
  min-width: 2.4rem;
  padding: 0.25rem 0.5rem;
  border-radius: 999px;
  background: #18181b;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  text-align: center;
}

.rm-dlg-pill.is-ready {
  background: #15803d;
}

.rm-dlg-check-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.rm-dlg-check-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.35rem 0.45rem;
  border-radius: 8px;
  background: #fff;
  border: 1px solid #ececee;
}

.rm-dlg-x {
  width: 1.85rem;
  height: 1.85rem;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #a1a1aa;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.rm-dlg-x:hover {
  background: #fef2f2;
  color: #b91c1c;
}

.rm-dlg-add-check {
  margin-top: 0.55rem;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.4rem 0.7rem;
  border-radius: 8px;
  border: 1px dashed #d4d4d8;
  background: transparent;
  color: #52525b;
  font-size: 0.78rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
}

.rm-dlg-add-check:hover {
  border-color: #18181b;
  color: #18181b;
  background: #fff;
}

.rm-seg {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  padding: 0.25rem;
  border-radius: 10px;
  background: #f4f4f5;
  border: 1px solid #e4e4e7;
}

.rm-seg-btn {
  flex: 1 1 auto;
  min-width: 3.6rem;
  padding: 0.45rem 0.55rem;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: #71717a;
  font-size: 0.78rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.rm-seg-btn:hover:not(:disabled) {
  color: #18181b;
  background: rgba(255, 255, 255, 0.7);
}

.rm-seg-btn.is-on {
  background: #18181b;
  color: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
}

.rm-seg-btn.is-on.is-ship {
  background: #15803d;
}

.rm-seg-btn.is-disabled,
.rm-seg-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.rm-dlg-footer {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  width: 100%;
}

.rm-dlg-footer-spacer {
  flex: 1;
}

.rm-dlg-primary,
.rm-dlg-ghost,
.rm-dlg-danger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  padding: 0.55rem 0.9rem;
  border-radius: 8px;
  border: 1px solid transparent;
  font-size: 0.84rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, opacity 0.15s ease;
}

.rm-dlg-primary {
  background: #18181b;
  color: #fff;
}

.rm-dlg-primary:hover:not(:disabled) {
  background: #09090b;
}

.rm-dlg-primary:disabled {
  opacity: 0.55;
  cursor: wait;
}

.rm-dlg-ghost {
  background: #fff;
  border-color: #e4e4e7;
  color: #52525b;
}

.rm-dlg-ghost:hover {
  border-color: #d4d4d8;
  color: #18181b;
}

.rm-dlg-danger {
  background: transparent;
  color: #b91c1c;
  padding-left: 0.55rem;
  padding-right: 0.55rem;
}

.rm-dlg-danger:hover:not(:disabled) {
  background: #fef2f2;
}

.rm-dlg-danger:disabled {
  opacity: 0.5;
}
</style>
